import React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { BrandCard } from './BrandCard';
import type { BrandWithMetrics } from '@/hooks/useAllBrandMetrics';

export interface BrandListColumnProps {
  brands: BrandWithMetrics[];
  selectedBrand: BrandWithMetrics | null;
  onSelectBrand: (brand: BrandWithMetrics) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onClearSearch: () => void;
  showOnlyActiveFilter: boolean;
  userCountry?: string | null;
}

export const BrandListColumn: React.FC<BrandListColumnProps> = ({
  brands,
  selectedBrand,
  onSelectBrand,
  searchTerm,
  onSearchChange,
  onClearSearch,
  showOnlyActiveFilter,
  userCountry,
}) => {
  return (
    <div className="lg:col-span-1 flex flex-col min-h-0 h-full">
      {/* Title & Search Bar (Fixed at top of column) */}
      <div className="shrink-0 space-y-4 mb-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Brands ({brands.length})
            {showOnlyActiveFilter && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                (with active campaigns)
              </span>
            )}
          </h2>
        </div>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={onClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Brands List (Scrollable) */}
      <div className="flex-1 overflow-y-auto min-h-0 space-y-4 no-scrollbar">
        {brands.length > 0 ? (
          brands.map((brand) => (
            <BrandCard
              key={brand.id}
              brand={brand}
              isSelected={selectedBrand?.id === brand.id}
              onSelect={onSelectBrand}
              userCountry={userCountry}
            />
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? (
              <>
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p>No brands found matching "{searchTerm}"</p>
                <button
                  type="button"
                  onClick={onClearSearch}
                  className="text-blue-600 hover:text-blue-700 mt-2"
                >
                  Clear search
                </button>
              </>
            ) : showOnlyActiveFilter ? (
              "No brands found with active campaigns."
            ) : (
              "No brands found. Add your first brand!"
            )}
          </div>
        )}
      </div>
    </div>
  );
};
